import { useCallback, useMemo, useState } from 'react'

import { twMerge } from 'tailwind-merge'

import Button from '@/components/common/Button'
import Switch from '@/components/common/Switch'
import TextInput from '@/components/form/TextInput'

export type LinkEditorPanelProps = {
  initialUrl?: string
  initialOpenInNewTab?: boolean
  className?: string
  onSetLink: (url: string, openInNewTab?: boolean) => void
}

export const useLinkEditorState = ({
  initialUrl,
  initialOpenInNewTab = true,
  onSetLink,
}: LinkEditorPanelProps) => {
  const [url, setUrl] = useState<string>(initialUrl || '')
  const [openInNewTab, setOpenInNewTab] = useState<boolean>(initialOpenInNewTab)

  const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(event.target.value)
  }, [])

  const isValidUrl = useMemo(() => {
    try {
      new URL(url)
      return true
    } catch (error) {
      return false
    }
  }, [url])

  const handleSubmit = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault()
      e?.stopPropagation()
      if (isValidUrl) {
        onSetLink(url, openInNewTab)
      }
    },
    [url, isValidUrl, openInNewTab, onSetLink],
  )

  const toggleOpenInNewTab = useCallback(() => {
    setOpenInNewTab(!openInNewTab)
  }, [openInNewTab, setOpenInNewTab])

  return {
    url,
    setUrl,
    openInNewTab,
    setOpenInNewTab,
    onChange,
    handleSubmit,
    isValidUrl,
    toggleOpenInNewTab,
  }
}

export const LinkEditorPanel: React.FC<LinkEditorPanelProps> = ({
  initialOpenInNewTab = true,
  initialUrl,
  className,
  onSetLink,
}) => {
  const state = useLinkEditorState({
    onSetLink,
    initialOpenInNewTab,
    initialUrl,
  })

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        e.stopPropagation()
        state.handleSubmit()
      }
    },
    [state.handleSubmit],
  )

  return (
    <div className={twMerge('flex flex-col w-full h-full', className)}>
      <div className="flex items-center w-full gap-2">
        <label className="flex items-center gap-2 w-full cursor-text">
          <TextInput
            type="url"
            placeholder="Enter URL"
            value={state.url}
            onChange={state.onChange}
            className="pl-[6px] h-7 rounded-[5px]"
            containerClassName="w-full"
            onKeyDown={handleKeyDown}
          />
        </label>
        <Button
          variant="primary-compact"
          type="button"
          disabled={!state.isValidUrl}
          onClick={state.handleSubmit}
        >
          Set Link
        </Button>
      </div>
      <div className="mt-2">
        <label className="flex items-center justify-between gap-2 text-[13px] font-medium cursor-pointer select-none text-primary-500 dark:text-primary-400">
          Open in new tab
          <Switch
            checked={state.openInNewTab}
            onClick={state.toggleOpenInNewTab}
            className="h-5"
            thumbClassName="w-4 h-4 data-[state=checked]:translate-x-[24px]"
          />
        </label>
      </div>
    </div>
  )
}
